import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getAuditLog } from '../services/auditApi';

// AUDIT-LOG v1: admin-only list of who changed what (every successful write in the software).
const FIELD = { padding: '7px 10px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-sm)', background: 'var(--color-surface)', color: 'var(--color-text-primary)' };
const TH = { textAlign: 'left', verticalAlign: 'bottom', padding: '8px 10px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' };
const TD = { padding: '8px 10px', fontSize: 'var(--font-size-sm)', borderBottom: '1px solid var(--color-border)', verticalAlign: 'top' };
const PAGE = 50;
const METHOD_COLOR = { POST: 'var(--color-success)', PUT: 'var(--color-warning)', PATCH: 'var(--color-warning)', DELETE: '#B42318' };
const LABEL = {
  'POST /follow-ups': 'Follow-up added', 'PUT /follow-ups/:id': 'Follow-up edited', 'DELETE /follow-ups/:id': 'Follow-up deleted',
  'PUT /dashboard/targets': 'Targets saved',
  'POST /complaints': 'Complaint added', 'PUT /complaints/:id/status': 'Complaint status changed',
  'POST /price-lists': 'Price list created', 'PUT /price-lists/:id': 'Price list edited', 'DELETE /price-lists/:id': 'Price list deleted',
  'POST /price-lists/:id/share': 'Price list shared', 'DELETE /price-lists/:id/share': 'Price list unshared',
  'POST /users': 'User created', 'POST /backups': 'Backup created', 'POST /backups/:id/restore': 'Backup restored',
  'POST /stock-analysis/orderless-stock/order/send': 'Order sent',
  'POST /ref-party-telegram': 'Telegram link saved', 'DELETE /ref-party-telegram/:id': 'Telegram link removed',
  'PUT /inactive-customers/customers/:id/reason': 'Inactive reason set',
};
const AREAS = ['follow-ups', 'dashboard', 'price-lists', 'complaints', 'users', 'roles', 'module-access', 'ref-party-telegram', 'backups', 'imports', 'company', 'locations', 'machines', 'stock-analysis', 'inactive-customers', 'search-config', 'low-stock-settings'];

const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return ymd(d); };
const when = (s) => (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(s || '') ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)} ${s.slice(11, 16)}` : (s || '—'));
const pretty = (d) => { try { return JSON.stringify(JSON.parse(d), null, 2); } catch (e) { return d; } };

export default function AuditLogPage() {
  const { user } = useAuth();
  const isAdmin = Boolean(user && user.role && (user.role.code === 'admin' || user.role.code === 'super_admin'));
  const [form, setForm] = useState({ from: daysAgo(6), to: ymd(new Date()), user: '', area: '', method: '', q: '' });
  const [applied, setApplied] = useState(form);
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const load = useCallback(() => {
    if (!isAdmin) return;
    setLoading(true);
    getAuditLog({ ...applied, limit: PAGE, offset })
      .then((d) => { setData(d); setError(''); })
      .catch((e) => setError((e && e.response && e.response.data && e.response.data.error && e.response.data.error.message) || 'Could not load the audit log.'))
      .then(() => setLoading(false));
  }, [isAdmin, applied, offset]);
  useEffect(() => { load(); }, [load]);

  if (!isAdmin) return <div style={{ padding: 16 }}>Only the admin can see the audit log.</div>;
  const search = (e) => { if (e) e.preventDefault(); setOffset(0); setOpen(null); setApplied({ ...form }); };
  const last = Math.min(offset + PAGE, data.total);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div>
        <h2 style={{ margin: 0 }}>Audit Log</h2>
        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>Who changed what, and when. Kept for 365 days. Passwords and tokens are never stored.</div>
      </div>
      <form onSubmit={search} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end' }}>
        <label style={{ fontSize: 12 }}>From<br /><input type="date" value={form.from} onChange={set('from')} style={FIELD} /></label>
        <label style={{ fontSize: 12 }}>To<br /><input type="date" value={form.to} onChange={set('to')} style={FIELD} /></label>
        <label style={{ fontSize: 12 }}>User<br /><input type="text" value={form.user} onChange={set('user')} placeholder="name" style={{ ...FIELD, width: 130 }} /></label>
        <label style={{ fontSize: 12 }}>Area<br />
          <select value={form.area} onChange={set('area')} style={FIELD}><option value="">All</option>{AREAS.map((a) => <option key={a} value={a}>{a}</option>)}</select>
        </label>
        <label style={{ fontSize: 12 }}>Type<br />
          <select value={form.method} onChange={set('method')} style={FIELD}><option value="">All</option><option value="POST">Added / done (POST)</option><option value="PUT">Edited (PUT)</option><option value="PATCH">Changed (PATCH)</option><option value="DELETE">Deleted (DELETE)</option></select>
        </label>
        <label style={{ fontSize: 12 }}>Search<br /><input type="text" value={form.q} onChange={set('q')} placeholder="text in details" style={{ ...FIELD, width: 150 }} /></label>
        <button type="submit" className="btn btn-primary">Search</button>
      </form>
      {error && <div style={{ color: '#B42318' }}>{error}</div>}
      <div style={{ overflowX: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', background: 'var(--color-surface)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={TH}>When</th><th style={TH}>User</th><th style={TH}>What</th><th style={TH}>Status</th><th style={TH}>Details</th></tr></thead>
          <tbody>
            {loading && <tr><td style={TD} colSpan={5}>Loading…</td></tr>}
            {!loading && data.items.length === 0 && <tr><td style={TD} colSpan={5}>No event found for these filters.</td></tr>}
            {!loading && data.items.map((x) => (
              <tr key={x.id}>
                <td style={{ ...TD, whiteSpace: 'nowrap' }}>{when(x.at)}</td>
                <td style={TD}><b>{x.user}</b>{x.party ? <div style={{ fontSize: 12, opacity: 0.75 }}>{x.party}</div> : null}</td>
                <td style={TD}>
                  <span style={{ color: METHOD_COLOR[x.method] || 'inherit', fontWeight: 700 }}>{LABEL[x.action] || x.action}</span>
                  <div style={{ fontSize: 12, opacity: 0.65 }}>{x.action}</div>
                </td>
                <td style={TD}>{x.status === 403 ? <span style={{ color: '#B42318', fontWeight: 600 }}>Refused (403)</span> : 'OK'}</td>
                <td style={TD}>
                  {x.detail ? (open === x.id
                    ? <div><button type="button" onClick={() => setOpen(null)} style={{ border: 'none', background: 'transparent', color: 'var(--color-primary, #2563eb)', cursor: 'pointer', padding: 0 }}>Hide</button><pre style={{ margin: '4px 0 0', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 420 }}>{pretty(x.detail)}</pre></div>
                    : <button type="button" onClick={() => setOpen(x.id)} style={{ border: 'none', background: 'transparent', color: 'var(--color-primary, #2563eb)', cursor: 'pointer', padding: 0 }}>Show</button>) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 'var(--font-size-sm)' }}>
        <button type="button" className="btn" disabled={offset === 0 || loading} onClick={() => { setOffset(Math.max(0, offset - PAGE)); setOpen(null); }}>Newer</button>
        <span>{data.total === 0 ? '0 events' : `${offset + 1}–${last} of ${data.total}`}</span>
        <button type="button" className="btn" disabled={last >= data.total || loading} onClick={() => { setOffset(offset + PAGE); setOpen(null); }}>Older</button>
      </div>
    </div>
  );
}
